import { memo, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";

import { useConnectionStore } from "stores/ConnectionStore";
import {
  Finding,
  useFindingStore,
  FindingStoreState,
} from "stores/FindingStore";
import { SubmitState } from "stores/lib";

import useAnalytics from "effect/use_analytics";

import Compare from "./Compare";
import Header from "./Header";
import {
  Entry,
  SeveritiesByEnum,
  SeveritiesDefaults,
  SeverityEntry,
  SourceEntry,
  SourcesByEnum,
  SourcesDefaults,
  StateEntry,
  StatesByEnum,
  StatesDefaults,
} from "./Header/Filters/data";
import Inbox from "./Inbox";

// -----------------------------------------------------------------------------

const OrderingComponentRegex = /^(-)?(.*)$/;

// -----------------------------------------------------------------------------

const OrganizationFindingsPage = memo(
  function OrganizationFindingsPage(): JSX.Element {
    useAnalytics("Findings: Findings Overview");

    //
    const [page, setPage] = useState(1);
    const [mobileMode, setMobileMode] = useState(false);
    const deeplinkFindings = useDeeplinkUrlFindings();
    const [findingsStore, findingsActions] = useFindingStore();
    const [connectionsStore] = useConnectionStore();
    const [findingsToCompare, setFindingsToCompare] = useState([] as Finding[]);
    const [searchText, setSearchText] = useState("");

    const [searchParams, setSearchParams] = useSearchParams();

    const [defaultSeverities, defaultStates, defaultSources] =
      useSearchFilters(searchParams);

    const [severities, setSeverities] = useState(defaultSeverities);
    const [states, setStates] = useState(defaultStates);
    const [sources, setSources] = useState(defaultSources);

    const ordering = (searchParams.get("ordering") ?? "-severity").trim();
    const [, defaultSortDirection, defaultSorting] =
      ordering.match(OrderingComponentRegex) ?? [];

    const [sorting, setSorting] = useState(defaultSorting);
    const [sortDirection, setSortDirection] = useState(
      defaultSortDirection ? 1 : 0
    );

    const findingsToCompareFiltered = findingsToCompare.filter(
      (finding) =>
        severities.some(
          (filter) => filter.all || filter.severity === finding.severity
        ) &&
        states.some((filter) => filter.all || filter.state === finding.state) &&
        sources.some((filter) => filter.all || filter.source === finding.source)
    );

    //
    useEffect(() => {
      setFindingsToCompare(findingsStore.deeplinkFindings);
    }, [findingsStore.deeplinkFindings]);

    useEffectOfLoadingData(
      page,
      setPage,
      deeplinkFindings,
      findingsStore,
      findingsActions,
      useMemoOfURLSearchParams()
    );
    useEffectOfSettingMobileMode(setMobileMode);
    useEffectOfSettingFirstFindingIfNothingSelected(
      findingsStore,
      findingsToCompareFiltered,
      setFindingsToCompare,
      mobileMode
    );
    useEffectOfUpdatingFindingsInUrl(
      findingsToCompareFiltered,
      deeplinkFindings
    );
    useEffectOfUpdatingUrlQuery(
      setSearchParams,
      findingsActions,
      severities,
      states,
      sources,
      sorting,
      sortDirection
    );

    //
    const isLoading =
      findingsStore.loadState === SubmitState.STARTED ||
      findingsStore.loadState === SubmitState.STARTED_AGAIN ||
      findingsStore.loadDeeplinkFindings === SubmitState.STARTED;
    const noFindings = !isLoading && findingsStore.findingsCount === 0;
    const allFindingsLoaded =
      findingsStore.findingsCount !== undefined
        ? findingsStore.findings.length >= findingsStore.findingsCount
        : false;
    const showOneConnectionNoFindingsWarning =
      noFindings && connectionsStore.connections?.size === 1;

    //
    function onFindingClick(
      finding: Finding,
      appendToList: boolean,
      removeFromList: boolean = false
    ) {
      setFindingsToCompare(
        basedOnUserActions(finding, appendToList, removeFromList, mobileMode)
      );
    }

    function onFindingRemove(uuid: string) {
      setFindingsToCompare(withoutASpecificUuid(uuid, mobileMode));
    }

    function loadNextPage() {
      if (allFindingsLoaded) {
        return;
      }

      if (findingsStore.findings.length === 0) {
        setPage(1);
        return;
      }

      setPage((page) => page + 1);
    }

    //
    return (
      <Stack sx={{ flex: 1 }}>
        <Header
          selectedFindings={findingsToCompareFiltered}
          resetSelectedFindings={onFindingClick}
          severities={severities}
          setSeverities={setSeverities}
          states={states}
          setStates={setStates}
          sources={sources}
          setSources={setSources}
          sorting={sorting}
          setSorting={setSorting}
          sortDirection={sortDirection}
          setSortDirection={setSortDirection}
          searchText={searchText}
          setSearchText={setSearchText}
          showFirstConnectionWarning={showOneConnectionNoFindingsWarning}
        />
        <Divider />

        <Stack direction="row" sx={{ flex: 1 }}>
          <Inbox
            isLoading={isLoading}
            allFindingsLoaded={allFindingsLoaded}
            noFindings={noFindings}
            findings={findingsStore.findings}
            selectedFindings={findingsToCompareFiltered}
            onFindingClick={onFindingClick}
            loadNextPage={loadNextPage}
            severities={severities}
            states={states}
            sources={sources}
            searchText={searchText}
          />
          <Compare
            isLoading={isLoading}
            mobileMode={mobileMode}
            errorMessage={
              findingsStore.findingsError ?? findingsStore.deeplinkFindingsError
            }
            findings={findingsToCompareFiltered}
            onFindingRemove={onFindingRemove}
          />
        </Stack>
      </Stack>
    );
  }
);

export default OrganizationFindingsPage;

// -----------------------------------------------------------------------------

function useMemoOfURLSearchParams() {
  const [searchParams] = useSearchParams();

  //
  return useMemo(() => searchParams, [searchParams]);
}

function useEffectOfLoadingData(
  page: number,
  setPage: Function,
  deeplinkFindings: string[],
  findingStore: FindingStoreState,
  findingsActions: any,
  params: URLSearchParams
) {
  const trigger = useMemo(() => {
    const triggerableParams = params;
    if (triggerableParams.get("page") === "0") {
      setPage(1);
    }

    triggerableParams.delete("finding");
    triggerableParams.delete("page");
    triggerableParams.sort();

    return triggerableParams.toString();
  }, [params, setPage]);

  // load deeplinkFindings first
  let effect = () => {};
  if (
    deeplinkFindings.length > 0 &&
    page === 1 &&
    findingStore.loadState === undefined &&
    findingStore.loadDeeplinkFindings === undefined
  ) {
    if (findingStore.loadDeeplinkFindings === undefined) {
      effect = () => {
        findingsActions.loadDeeplinkFindings(deeplinkFindings);
      };
    }
    // then load regular results
  } else if (
    deeplinkFindings.length < 1 ||
    findingStore.loadDeeplinkFindings === SubmitState.SUCCESS
  ) {
    effect = () => {
      const newPage = params.get("page") === "0" ? 1 : page;
      params.set("page", String(newPage));
      findingsActions.load(newPage, params);
    };
  }

  //
  useEffect(effect, [page, trigger, findingStore.loadDeeplinkFindings]); // eslint-disable-line
}

function useDeeplinkUrlFindings() {
  const [searchParams] = useSearchParams();

  //
  return useMemo(() => {
    return searchParams.getAll("finding");
  }, [searchParams]);
}

function useEffectOfSettingFirstFindingIfNothingSelected(
  findingsStore: FindingStoreState,
  findingsToCompare: Finding[],
  setFindingsToCompare: Function,
  mobileMode: boolean
) {
  useEffect(() => {
    if (
      findingsStore.findings.length > 0 &&
      findingsToCompare.length < 1 &&
      findingsStore.loadState === SubmitState.SUCCESS &&
      !mobileMode
    ) {
      setFindingsToCompare([findingsStore.findings[0]]);
    }
  }, [
    findingsStore.loadState,
    findingsStore.findings,
    findingsToCompare,
    setFindingsToCompare,
    mobileMode,
  ]);
}

function useEffectOfSettingMobileMode(setMobileMode: Function) {
  useEffect(() => {
    function handleResize() {
      setMobileMode(window.innerWidth <= 640);
    }

    //
    window.addEventListener("resize", handleResize);
    handleResize();

    //
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [setMobileMode]);
}

function useEffectOfUpdatingFindingsInUrl(
  findingsToCompare: Finding[],
  deeplinkFindings: string[]
) {
  const [, setSearchParams] = useSearchParams();

  //
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    if (
      params.getAll("finding").length === findingsToCompare.length &&
      findingsToCompare.every((finding: Finding) =>
        deeplinkFindings.includes(finding.uuid)
      )
    ) {
      return;
    }

    //
    params.delete("finding");

    if (params.get("page") === "0") {
      params.set("page", "1");
    }

    findingsToCompare.forEach((finding) =>
      params.append("finding", finding.uuid)
    );

    //
    params.sort();

    //
    setSearchParams(params.toString());
  }, [setSearchParams, findingsToCompare]); // eslint-disable-line
}

// -----------------------------------------------------------------------------

function basedOnUserActions(
  finding: Finding,
  appendToList: boolean,
  removeFromList: boolean,
  mobileMode: boolean
) {
  return (findingsToCompare: Finding[]) => {
    const length = findingsToCompare.length;
    const isNewItem = !findingsToCompare.includes(finding);

    //
    switch (true) {
      case removeFromList && (length > 1 || mobileMode):
        return findingsToCompare.filter((f) => f.uuid !== finding.uuid);

      case !appendToList && !removeFromList:
        return [finding];

      case length >= 4:
      case appendToList && removeFromList && length < 2 && !mobileMode:
      case appendToList && !removeFromList && !isNewItem:
        return findingsToCompare;

      case appendToList && !removeFromList && isNewItem:
        return [...findingsToCompare, finding];

      default:
        return [finding];
    }
  };
}

function withoutASpecificUuid(uuidToExclude: string, mobileMode: boolean) {
  function outThisUuid(finding: Finding) {
    return finding.uuid !== uuidToExclude;
  }

  //
  return (findingsToCompare: Finding[]) => {
    return findingsToCompare.length > 1 || mobileMode
      ? findingsToCompare.filter(outThisUuid)
      : findingsToCompare;
  };
}

// -----------------------------------------------------------------------------

function useSearchFilters(
  searchParams: URLSearchParams
): [SeverityEntry[], StateEntry[], SourceEntry[]] {
  return useMemo(() => parseSearchParams(searchParams), [searchParams]);
}

function parseSearchParams(
  params: URLSearchParams
): [SeverityEntry[], StateEntry[], SourceEntry[]] {
  const severities: SeverityEntry[] = [];
  const states: StateEntry[] = [];
  const sources: SourceEntry[] = [];

  //
  params.forEach((value, key) => {
    switch (key) {
      case "severity": {
        const severity = SeveritiesByEnum[parseInt(value, 10) as any];
        severity && severities.push(severity);
        break;
      }

      case "state": {
        const state = StatesByEnum[parseInt(value, 10) as any];
        state && states.push(state);
        break;
      }

      case "source": {
        const source = SourcesByEnum[parseInt(value, 10) as any];
        sources?.push(source);
        break;
      }
    }
  });

  //
  return [
    severities.length > 0 ? severities : SeveritiesDefaults,
    states.length > 0 ? states : StatesDefaults,
    sources.length > 0 ? sources : SourcesDefaults,
  ];
}

function useEffectOfUpdatingUrlQuery(
  setSearchParams: any,
  findingsActions: any,
  severities: SeverityEntry[],
  states: StateEntry[],
  sources: SourceEntry[],
  sorting: string,
  sortDirection: number
): void {
  useEffect(() => {
    const oldParams = new URLSearchParams(window.location.search);
    oldParams.delete("finding");
    oldParams.sort();

    //
    const params = new URLSearchParams(window.location.search);
    params.delete("severity");
    params.delete("state");
    params.delete("source");
    params.delete("finding");

    appendParamsIfNotAll(params, severities, "severity");
    appendParamsIfNotAll(params, states, "state");
    appendParamsIfNotAll(params, sources, "source");
    params.set("ordering", `${sortDirection ? "-" : ""}${sorting}`);

    params.sort();

    //
    if (oldParams.toString() === params.toString()) {
      return;
    }

    params.set("page", "0");

    //
    setSearchParams(params.toString());
    findingsActions.clearDeeplinkFindings();
  }, [
    setSearchParams,
    findingsActions,
    severities,
    states,
    sources,
    sorting,
    sortDirection,
  ]);
}

function appendParamsIfNotAll(
  params: URLSearchParams,
  filters: Entry[],
  key: string
) {
  if (!filters[0]?.all) {
    filters.forEach(({ [key]: value }: Entry) =>
      params.append(key, String(value))
    );
  }
}
